import React from "react";
import { Box, InputAdornment, Menu, MenuItem } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { MuiDecimalField } from "../../../../../components/formsComponents/MuiDecimalField";
import { TaxTypeSelector, TaxType } from "./TaxTypeSelector";

const GST_PRESETS = ["0", "5", "12", "18", "28"];

interface ITaxPercentageFieldProps {
  taxType: TaxType;
  percentage: number | string | null | undefined;
  onTaxTypeChange: (next: TaxType) => void;
  onPercentageChange: (value: string) => void;
  name?: string;
  disabled?: boolean;
}

/**
 * Unified tax-percentage field for both GST and VAT so they render identically
 * (same MuiDecimalField base → same height/padding). The tax type is a clickable
 * prefix; the "%" is a bold suffix.
 *
 * - VAT: the value is free-text (type any percentage).
 * - GST: the value is chosen from preset rates (0/5/12/18/28) via a popup that
 *   opens on the value area; the input itself is read-only.
 */
export const TaxPercentageField: React.FC<ITaxPercentageFieldProps> = ({
  taxType,
  percentage,
  onTaxTypeChange,
  onPercentageChange,
  name = "tax_percentage",
  disabled = false,
}) => {
  const isGst = taxType === "GST";
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const presetOpen = Boolean(anchorEl);

  const openPresets = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
  };
  const closePresets = () => setAnchorEl(null);

  const selectPreset = (val: string) => () => {
    setAnchorEl(null);
    onPercentageChange(val);
  };

  return (
    <>
      <MuiDecimalField
        name={name}
        value={percentage}
        max={100}
        placeholder="0"
        disabled={disabled}
        onChange={(e) => onPercentageChange(e.target.value)}
        sx={{
          minWidth: 80,
          maxWidth: 130,
          // Kill the container's default padding so the tax-type label hugs
          // the left border with no extra spacing. Fixed 36px field height.
          "& .MuiOutlinedInput-root": {
            pl: 0.75,
            pr: 0.75,
            height: 36,
          },
          // Light-bordered box around the editable value (common to GST & VAT).
          // flex:1 makes the value area fill the remaining width so multi-digit
          // values (e.g. 100) are always fully visible.
          "& .MuiInputBase-input": {
            flex: 1,
            minWidth: 0,
            width: "auto",
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            textAlign: "center",
            mx: 0.5,
            px: 0.5,
            py: 0,
            height: 36,
            boxSizing: "border-box",
            // GST value is chosen from presets → show it as a button.
            cursor: isGst && !disabled ? "pointer" : "text",
          },
        }}
        InputProps={{
          // GST value is read-only (selected via preset popup); VAT is editable.
          readOnly: isGst,
          onClick: isGst ? openPresets : undefined,
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <TaxTypeSelector
                value={taxType}
                onChange={onTaxTypeChange}
                disabled={disabled}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                pl: 0,
                ml: 0,
                "& .MuiTypography-root": {
                  fontWeight: 700,
                  color: "text.secondary",
                },
              }}
            >
              {isGst && !disabled && (
                <ArrowDropDownIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", mr: 0.25 }}
                />
              )}
              <Box component="span" sx={{ fontWeight: 700, color: "text.secondary" }}>
                %
              </Box>
            </InputAdornment>
          ),
        }}
      />

      {/* GST preset rates popup */}
      <Menu
        anchorEl={anchorEl}
        open={presetOpen}
        onClose={closePresets}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { minWidth: 90, mt: 0.5 } }}
      >
        {GST_PRESETS.map((rate) => (
          <MenuItem
            key={rate}
            selected={String(percentage ?? "") === rate}
            onClick={selectPreset(rate)}
          >
            {rate} %
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
